import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP } from '@/lib/gsap'
import { formatCompactCurrency } from '@/lib/format'
import { LogoMark } from '@/components/generals/logo'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { ButtonLink } from '@/components/generals/button'
import { RevealText } from '@/components/generals/reveal-text'
import { Sparkline } from '@/components/charts/sparkline'
import { DeltaBadge } from '@/components/charts/delta-badge'

import {
    BookOpen,
    Check,
    CreditCard,
    Landmark,
    Receipt,
    Sparkles,
    Users
} from 'lucide-react'

import { CHART_COLORS, FORECAST_SCENARIOS } from '@/constants/dashboard'

const SOURCES = [
    { label: 'Bank accounts', Icon: Landmark, y: 12 },
    { label: 'Cards', Icon: CreditCard, y: 31 },
    { label: 'Accounting', Icon: BookOpen, y: 50 },
    { label: 'Payroll', Icon: Users, y: 69 },
    { label: 'Billing', Icon: Receipt, y: 88 }
]

const ConnectDiagram: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='relative h-[240px] overflow-hidden rounded-2xl border border-burgundy/10 bg-ivory p-4'>
            <svg viewBox='0 0 320 240' preserveAspectRatio='none' className='absolute inset-0 size-full'>
                <g fill='none' strokeWidth='1.2' strokeLinecap='round'>
                    {SOURCES.map(source => {
                        const y = (source.y / 100) * 240
                        const d = `M150 ${y} C210 ${y} 205 120 262 120`

                        return (
                            <g key={source.label}>
                                <path d={d} stroke='rgb(57 26 24 / 0.16)' />
                                <path d={d} stroke='rgb(158 129 85 / 0.9)' strokeWidth='2' className='flow-packet' style={{ animationDuration: '6s', animationDelay: `${source.y * -0.05}s` }} />
                            </g>
                        )
                    })}
                </g>
            </svg>

            {SOURCES.map(({ label, Icon, y }) => (
                <span
                    key={label}
                    className='absolute left-4 flex -translate-y-1/2 items-center gap-2 rounded-full border border-burgundy/10 bg-paper py-1 pl-1 pr-3 text-[11px] font-medium text-burgundy'
                    style={{ top: `${y}%` }}
                >
                    <span className='flex size-5 items-center justify-center rounded-full bg-cream text-burgundy'>
                        <Icon className='size-3' />
                    </span>
                    {label}
                </span>
            ))}

            <span className='absolute right-5 top-1/2 flex size-14 -translate-y-1/2 items-center justify-center rounded-full bg-burgundy shadow-[0_0_0_8px_rgb(57_26_24/0.06)]'>
                <LogoMark className='size-7 text-gold' />
            </span>
        </div>
    )
}

const RULES = [
    'Categorize new transactions',
    'Match receipts from the inbox',
    'Reconcile bank accounts',
    'Draft the month-end pack'
]

const AutomateDiagram: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='h-[240px] rounded-2xl border border-burgundy/10 bg-ivory p-4' data-chart=''>
            <p className='flex items-center justify-between text-xs font-medium text-burgundy'>
                Automations
                <span className='rounded-full bg-sage/25 px-2 py-0.5 text-[10px] text-sage-dark'>4 running</span>
            </p>

            <ul className='mt-4 space-y-3'>
                {RULES.map((rule, index) => (
                    <li key={rule}>
                        <div className='flex items-center gap-2.5 text-[11px] text-stone-dark'>
                            <span
                                className='chart-fade flex size-4 items-center justify-center rounded-full bg-burgundy text-cream'
                                style={{ '--chart-delay': `${600 + index * 450}ms` } as CSSProperties}
                            >
                                <Check className='size-2.5' />
                            </span>
                            {rule}
                        </div>

                        <div className='ml-[26px] mt-1.5 h-1 rounded-full bg-burgundy/[0.07]'>
                            <span
                                className='chart-grow-x block h-full rounded-full bg-gold'
                                style={{ '--chart-delay': `${index * 450}ms` } as CSSProperties}
                            />
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const DecideDiagram: FC = (): ReactNode => {
    const projection = FORECAST_SCENARIOS.growth.values

    return (
        <div aria-hidden='true' className='h-[240px] rounded-2xl border border-burgundy/10 bg-ivory p-4'>
            <p className='text-[11px] text-stone-dark'>Q1 cash outlook with planned hires</p>

            <div className='mt-1.5 flex items-center gap-2'>
                <span className='font-heading text-2xl font-medium tracking-[-0.03em] text-burgundy'>
                    {formatCompactCurrency(projection[projection.length - 1])}
                </span>

                <DeltaBadge value={40.3} suffix='vs today' />
            </div>

            <Sparkline values={[2486400, ...projection]} color={CHART_COLORS.sage} height={64} className='mt-3' />

            <div className='mt-4 flex items-start gap-2 rounded-xl bg-burgundy p-3 text-cream'>
                <Sparkles className='mt-0.5 size-3.5 shrink-0 text-gold' />

                <p className='text-[11px] leading-relaxed text-cream/85'>
                    Hiring two engineers in November keeps cash above $2.6M all quarter.
                </p>
            </div>
        </div>
    )
}

const STEPS = [
    {
        number: '01',
        title: 'Connect your accounts',
        body: 'Link banks, cards, your accounting system, payroll and billing in minutes through secure, read-only connections. Veyra backfills your history automatically.',
        Diagram: ConnectDiagram
    },
    {
        number: '02',
        title: 'Automate your finances',
        body: 'Veyra categorizes every transaction, matches receipts, reconciles accounts and drafts recurring reports, then routes anything unusual to the right person.',
        Diagram: AutomateDiagram
    },
    {
        number: '03',
        title: 'Make smarter decisions',
        body: 'Live dashboards, rolling forecasts and AI insights show where you stand and what to do next, so every decision starts from current, trusted numbers.',
        Diagram: DecideDiagram
    }
]

export const HowItWorks: FC = (): ReactNode => {
    const stepsRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const steps = stepsRef.current

        if (!steps) return

        const media = gsap.matchMedia()

        media.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
            const fill = steps.querySelector('[data-progress-fill]')
            const dots = steps.querySelectorAll('[data-step-dot]')

            if (fill) {
                gsap.fromTo(fill, { scaleX: 0 }, {
                    scaleX: 1,
                    ease: 'none',
                    scrollTrigger: { trigger: steps, start: 'top 75%', end: 'bottom 75%', scrub: 0.6 }
                })
            }

            dots.forEach((dot, index) => {
                gsap.fromTo(dot, { backgroundColor: '#E9E6D8', color: '#391A18' }, {
                    backgroundColor: '#391A18',
                    color: '#E9E6D8',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: steps,
                        start: () => `top+=${Math.round(steps.offsetHeight * index * 0.34)} 75%`,
                        end: () => `top+=${Math.round(steps.offsetHeight * (index * 0.34 + 0.05))} 75%`,
                        scrub: true,
                        invalidateOnRefresh: true
                    }
                })
            })
        })

        return () => media.revert()
    }, { scope: stepsRef })

    return (
        <section id='how-it-works' aria-labelledby='how-it-works-title' className='bg-cream'>
            <Container className='py-28 md:py-40'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <Eyebrow>How it works</Eyebrow>

                        <RevealText
                            as='h2'
                            id='how-it-works-title'
                            text='From connected accounts to confident decisions.'
                            highlight={['confident decisions.']}
                            highlightClassName='text-stone'
                            className='mt-6 font-heading text-display-xl font-medium text-burgundy'
                        />
                    </div>

                    <div data-reveal='' className='lg:col-span-4 lg:col-start-9'>
                        <p className='text-lg leading-relaxed text-stone-dark'>
                            Three steps and most teams are live the same day. No migration project, no consultants, no new spreadsheet to maintain.
                        </p>

                        <ButtonLink href='/demo' variant='secondary' className='mt-8' arrow>
                            See it with your data
                        </ButtonLink>
                    </div>
                </div>

                <div ref={stepsRef} className='relative mt-20 md:mt-28'>
                    <div aria-hidden='true' className='absolute left-0 right-0 top-[22px] hidden h-px bg-burgundy/15 lg:block'>
                        <span data-progress-fill='' className='block h-full origin-left bg-burgundy' />
                    </div>

                    <ol className='grid gap-16 lg:grid-cols-3 lg:gap-10'>
                        {STEPS.map(({ number, title, body, Diagram }, index) => (
                            <li
                                key={number}
                                data-reveal=''
                                className='relative'
                                style={{ '--reveal-delay': `${index * 140}ms` } as CSSProperties}
                            >
                                <span
                                    data-step-dot=''
                                    className={cn(
                                        'relative z-10 flex size-11 items-center justify-center rounded-full border border-burgundy/20 bg-cream font-mono text-xs font-medium text-burgundy'
                                    )}
                                >
                                    {number}
                                </span>

                                <h3 className='mt-8 font-heading text-display-sm font-medium text-burgundy'>{title}</h3>
                                <p className='mt-4 max-w-sm leading-relaxed text-stone-dark'>{body}</p>

                                <div className='mt-8'>
                                    <Diagram />
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </Container>
        </section>
    )
}
